import React, { useState } from 'react';
import { FileManagerProps, FileInfo } from '../../../types/certificate';
import { useFileManager } from '../hooks/useFileManager';
import { formatFileSize, formatDate } from '../../../services/fileService';
import styles from './FileManager.module.css';

/**
 * 文件管理组件
 * 提供文件列表展示、搜索、排序、删除等功能
 */
export const FileManager: React.FC<FileManagerProps> = ({
  className = ''
}) => {
  const {
    files,
    loading,
    error,
    query,
    total,
    currentPage,
    totalPages,
    updateQuery,
    refreshFiles,
    deleteFile,
    downloadFile
  } = useFileManager();

  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);

  // 处理文件类型筛选
  const handleTypeFilter = (type: 'all' | 'certificate' | 'report') => {
    updateQuery({ type, page: 1 });
  };

  // 处理搜索
  const handleSearch = () => {
    updateQuery({ orderNumber: searchTerm, page: 1 });
  };

  // 处理排序
  const handleSort = (sortBy: 'createdAt' | 'filename' | 'size') => {
    const sortOrder = query.sortBy === sortBy && query.sortOrder === 'desc' ? 'asc' : 'desc';
    updateQuery({ sortBy, sortOrder });
  };

  // 处理分页
  const handlePageChange = (page: number) => {
    updateQuery({ page });
  };

  // 处理文件选择
  const handleFileSelect = (fileId: string, checked: boolean) => {
    const newSelected = new Set(selectedFiles);
    if (checked) {
      newSelected.add(fileId);
    } else {
      newSelected.delete(fileId);
    }
    setSelectedFiles(newSelected);
  };

  // 全选/取消全选
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedFiles(new Set(files.map(f => f.id)));
    } else {
      setSelectedFiles(new Set());
    }
  };

  // 删除文件
  const handleDeleteFile = async (fileId: string) => {
    setFileToDelete(fileId);
    setShowDeleteModal(true);
  };

  // 确认删除
  const confirmDelete = async () => {
    if (fileToDelete) {
      try {
        await deleteFile(fileToDelete);
        setShowDeleteModal(false);
        setFileToDelete(null);
      } catch (error) {
        alert('删除失败，请重试');
      }
    }
  };

  // 获取文件类型图标
  const getFileTypeIcon = (file: FileInfo) => {
    if (file.type === 'certificate') {
      return '📜';
    } else if (file.type === 'report') {
      return '📊';
    }
    return '📄';
  };

  // 获取状态图标
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ready':
        return '✅';
      case 'generating':
        return '⏳';
      case 'error':
        return '❌';
      default:
        return '❓';
    }
  };

  return (
    <div className={`${styles.fileManager} ${className}`}>
      {/* 页头 */}
      <div className={styles.header}>
        <h2 className={styles.title}>文件管理</h2>
        <button 
          className={styles.refreshButton}
          onClick={refreshFiles}
          disabled={loading}
        >
          🔄 刷新
        </button>
      </div>

      {/* 工具栏 */}
      <div className={styles.toolbar}>
        {/* 搜索框 */}
        <div className={styles.searchBox}>
          <input
            type="text"
            placeholder="搜索订单号..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className={styles.searchInput}
          />
          <button 
            onClick={handleSearch}
            className={styles.searchButton}
            disabled={loading}
          >
            🔍
          </button>
        </div>

        {/* 类型筛选 */}
        <div className={styles.typeFilter}>
          <button
            className={`${styles.filterButton} ${query.type === 'all' ? styles.active : ''}`}
            onClick={() => handleTypeFilter('all')}
          >
            全部 ({total})
          </button>
          <button
            className={`${styles.filterButton} ${query.type === 'certificate' ? styles.active : ''}`}
            onClick={() => handleTypeFilter('certificate')}
          >
            📜 合格证
          </button>
          <button
            className={`${styles.filterButton} ${query.type === 'report' ? styles.active : ''}`}
            onClick={() => handleTypeFilter('report')}
          >
            📊 检测报告
          </button>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className={styles.error}>
          ❌ {error}
          <button onClick={refreshFiles} className={styles.retryButton}>
            重试
          </button>
        </div>
      )}

      {/* 加载状态 */}
      {loading && (
        <div className={styles.loading}>
          ⏳ 加载中...
        </div>
      )}

      {/* 文件列表 */}
      {!loading && !error && (
        <>
          {/* 批量操作栏 */}
          {selectedFiles.size > 0 && (
            <div className={styles.bulkActions}>
              <span>已选择 {selectedFiles.size} 个文件</span>
              <button className={styles.bulkDeleteButton}>
                🗑️ 批量删除
              </button>
            </div>
          )}

          {/* 表格 */}
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.checkboxHeader}>
                    <input
                      type="checkbox"
                      checked={files.length > 0 && selectedFiles.size === files.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </th>
                  <th>类型</th>
                  <th>状态</th>
                  <th 
                    className={styles.sortableHeader}
                    onClick={() => handleSort('filename')}
                  >
                    文件名 {query.sortBy === 'filename' && (query.sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>订单号</th>
                  <th 
                    className={styles.sortableHeader}
                    onClick={() => handleSort('size')}
                  >
                    大小 {query.sortBy === 'size' && (query.sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className={styles.sortableHeader}
                    onClick={() => handleSort('createdAt')}
                  >
                    创建时间 {query.sortBy === 'createdAt' && (query.sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {files.map((file) => (
                  <tr key={file.id} className={styles.tableRow}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedFiles.has(file.id)}
                        onChange={(e) => handleFileSelect(file.id, e.target.checked)}
                      />
                    </td>
                    <td>
                      <span className={styles.fileType}>
                        {getFileTypeIcon(file)}
                        {file.type === 'certificate' ? '合格证' : '检测报告'}
                      </span>
                    </td>
                    <td>
                      <span className={styles.status}>
                        {getStatusIcon(file.status)}
                        {file.status === 'ready' ? '就绪' : 
                         file.status === 'generating' ? '生成中' : '错误'}
                      </span>
                    </td>
                    <td className={styles.filename}>
                      {file.thumbnailUrl && (
                        <img 
                          src={file.thumbnailUrl} 
                          alt="预览" 
                          className={styles.thumbnail}
                        />
                      )}
                      <span>{file.filename}</span>
                    </td>
                    <td className={styles.orderNumber}>{file.orderNumber}</td>
                    <td>{formatFileSize(file.size)}</td>
                    <td>{formatDate(file.createdAt)}</td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={styles.actionButton}
                          onClick={async () => {
                            try {
                              await downloadFile(file);
                            } catch (error) {
                              alert('下载失败: ' + (error instanceof Error ? error.message : '未知错误'));
                            }
                          }}
                          title="下载文件"
                          disabled={file.status !== 'ready'}
                        >
                          ⬇️
                        </button>
                        <button
                          className={styles.actionButton}
                          onClick={() => {
                            // 直接在新标签页中打开文件URL
                            window.open(file.url, '_blank');
                          }}
                          title="在新标签页中打开"
                          disabled={file.status !== 'ready'}
                        >
                          🔗
                        </button>
                        <button
                          className={`${styles.actionButton} ${styles.deleteButton}`}
                          onClick={() => handleDeleteFile(file.id)}
                          title="删除文件"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 空状态 */}
          {files.length === 0 && (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📁</div>
              <h3>暂无文件</h3>
              <p>还没有生成任何文件，去创建一个合格证吧！</p>
            </div>
          )}

          {/* 分页 */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageButton}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ⬅️ 上一页
              </button>
              
              <span className={styles.pageInfo}>
                第 {currentPage} 页，共 {totalPages} 页
              </span>
              
              <button
                className={styles.pageButton}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                下一页 ➡️
              </button>
            </div>
          )}
        </>
      )}

      {/* 删除确认模态框 */}
      {showDeleteModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>确认删除</h3>
            <p>确定要删除这个文件吗？此操作不可撤销。</p>
            <div className={styles.modalActions}>
              <button 
                className={styles.cancelButton}
                onClick={() => setShowDeleteModal(false)}
              >
                取消
              </button>
              <button 
                className={styles.confirmButton}
                onClick={confirmDelete}
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};